import { PROXY_BASE } from '../config'
import type { ProxyErrorBody } from './types'

/**
 * Error taxonomy driving the enforcement-aware UI states (PRD P0.7).
 * Classification is primarily by HTTP status — the proxy is the single
 * enforcement point and the viewer never invents rules — with the error
 * body's `code` only disambiguating the two 403 flavors and the two 429 flavors.
 * Note: viewer traffic is exempt from daily request quotas by design
 * (PRD P0.1, revised D2). A 429 with code seat_limit_reached is a blocking
 * seat error; any other 429 (edge rate-limit, Cloudflare) is treated as a
 * transient service error so the map is not permanently blocked by a non-seat 429.
 */
export type ErrorKind =
  | 'auth' // 401 — session missing/expired
  | 'seat' // 429 — concurrent seat limit (code: seat_limit_reached)
  | 'ratelimit' // 429 — edge rate-limit (Cloudflare/nginx, no proxy body)
  | 'subscription' // 403 — company lifecycle inactive
  | 'plan' // 403 — plan lacks allows_map_viewer
  | 'layer' // 403 — layer not in company.allowed_layers
  | 'service' // 403 — plan lacks the OGC service (e.g. WFS for vector mode, P1.1)
  | 'unavailable' // 503
  | 'unknown'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly kind: ErrorKind,
    message: string,
    public readonly retryAfterSec: number | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Stable, client-readable code for an error surface. Format: KIND-STATUS
// (e.g. SEAT-429). status 0 is the sentinel for a pure JS crash (see crashGuard).
const CODE_PREFIX: Record<ErrorKind, string> = {
  auth: 'AUTH',
  seat: 'SEAT',
  ratelimit: 'RATE',
  subscription: 'SUB',
  plan: 'PLAN',
  layer: 'LAYER',
  service: 'SVC',
  unavailable: 'DOWN',
  unknown: 'ERR',
}

export function errorCode(kind: ErrorKind, status: number): string {
  if (status === 0) return 'JS-CRASH'
  return `${CODE_PREFIX[kind]}-${status}`
}

function classify(status: number, body: ProxyErrorBody): ErrorKind {
  if (status === 401) return 'auth'
  if (status === 429) return (body.code ?? '').toLowerCase().includes('seat') ? 'seat' : 'ratelimit'
  if (status === 403) {
    const code = (body.code ?? '').toLowerCase()
    if (code.includes('map_viewer')) return 'plan'
    if (code.includes('layer')) return 'layer'
    if (code.includes('service')) return 'service'
    if (code.includes('sql')) return 'service' // P2: feature-scoped, never a full-screen state
    return 'subscription'
  }
  if (status === 503) return 'unavailable'
  return 'unknown'
}

function messageOf(body: ProxyErrorBody, fallback: string): string {
  return body.message ?? body.error ?? body.detail ?? fallback
}

export async function parseErrorResponse(res: Response): Promise<ApiError> {
  let body: ProxyErrorBody = {}
  try {
    body = await res.clone().json()
  } catch {
    // non-JSON error body (e.g. GeoServer XML exception) — classify by status alone
  }
  const retryAfter = res.headers.get('Retry-After')
  return new ApiError(
    res.status,
    classify(res.status, body),
    messageOf(body, `Request failed (HTTP ${res.status})`),
    retryAfter !== null && !Number.isNaN(Number(retryAfter)) ? Number(retryAfter) : null,
  )
}

/**
 * Fetch against the proxy with credentials included (HttpOnly session cookie —
 * the viewer itself stores no credential of any kind, PRD P0.5).
 * Throws ApiError on any non-2xx response.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(PROXY_BASE + path, { credentials: 'include', ...init })
  if (!res.ok) throw await parseErrorResponse(res)
  return res
}
